/** docs/UX.md 17 · `/m/export` 导出 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AuditFinding } from "@xinghai/shared";
import {
  Button,
  EmptyState,
  Field,
  Icon,
  Input,
  Sheet,
  Table,
  Textarea,
  useToast,
} from "@xinghai/ui";
import type { Column } from "@xinghai/ui";
import { batchesApi, exportApi } from "../../lib/api";
import { qk } from "../../lib/queries";
import {
  CheckboxPicker,
  ErrorNotice,
  LoadingBlock,
  PageHeader,
  QueryBoundary,
  Section,
  StatRow,
  StatusTag,
  formatDateTime,
} from "./common";
import { BATCH_STATE_LABEL, auditCodeLabel } from "./labels";
import type { ExportPreviewResponse, ExportVersionItem } from "./types";

const SEMVER = /^\d+\.\d+\.\d+$/;

/** Splits a pasted blob of recording ids on commas / whitespace / newlines. */
export function parseRecordingIds(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/[\s,;，、]+/u)
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  ];
}

export function groupFindings(
  findings: AuditFinding[],
): Array<{ code: string; items: AuditFinding[] }> {
  const map = new Map<string, AuditFinding[]>();
  for (const f of findings) {
    const list = map.get(f.code) ?? [];
    list.push(f);
    map.set(f.code, list);
  }
  return [...map.entries()]
    .map(([code, items]) => ({ code, items }))
    .sort((a, b) => b.items.length - a.items.length || a.code.localeCompare(b.code));
}

export function ExportPage() {
  const toast = useToast();
  const qc = useQueryClient();
  const [batchIds, setBatchIds] = useState<string[]>([]);
  const [idsText, setIdsText] = useState("");
  const [preview, setPreview] = useState<ExportPreviewResponse | null>(null);
  const [freezeOpen, setFreezeOpen] = useState(false);

  const batches = useQuery({
    queryKey: qk.batches,
    queryFn: ({ signal }) => batchesApi.list(signal),
  });
  const versions = useQuery({
    queryKey: qk.exportVersions,
    queryFn: ({ signal }) => exportApi.list(signal),
  });

  const recordingIds = useMemo(() => parseRecordingIds(idsText), [idsText]);
  const hasSelection = batchIds.length > 0 || recordingIds.length > 0;

  const runPreview = useMutation({
    mutationFn: () =>
      exportApi.preview({
        ...(recordingIds.length > 0 ? { recording_ids: recordingIds } : {}),
        ...(batchIds.length > 0 ? { batch_ids: batchIds } : {}),
        include_induced: false,
      }),
    onSuccess: (r) => {
      setPreview(r);
      toast.info(
        r.ok ? `审计通过，${r.rows} 行可以冻结` : `审计发现 ${r.errors.length} 条红线错误`,
      );
    },
    onError: (e: Error) => {
      setPreview(null);
      toast.error(e.message);
    },
  });

  const errorGroups = useMemo(() => groupFindings(preview?.errors ?? []), [preview]);
  const warningGroups = useMemo(() => groupFindings(preview?.warnings ?? []), [preview]);
  const canFreeze =
    preview !== null && preview.ok && preview.errors.length === 0 && preview.rows > 0;

  const versionColumns: Array<Column<ExportVersionItem>> = [
    {
      key: "version",
      header: "版本",
      render: (v) => <span className="xz-adm__mono">{v.version}</span>,
    },
    { key: "rows", header: "录音数", align: "end", render: (v) => v.recordings },
    {
      key: "ok",
      header: "审计",
      render: (v) => (
        <StatusTag
          icon={v.ok ? "ui-check-circle" : "ui-warning"}
          tone={v.ok ? "success" : "danger"}
        >
          {v.ok ? "全绿" : "有错误"}
        </StatusTag>
      ),
    },
    { key: "frozen_at", header: "冻结时间", render: (v) => formatDateTime(v.frozen_at) },
    {
      key: "links",
      header: "下载",
      render: (v) => (
        <span className="xz-exp__links">
          <a
            className="gz-button xz-button--sm"
            href={exportApi.downloadUrl(v.version, "manifest")}
            download
          >
            <Icon id="ui-download" size={16} /> manifest.jsonl
          </a>
          <a
            className="gz-button xz-button--sm"
            href={exportApi.downloadUrl(v.version, "product_materials")}
            download
          >
            <Icon id="ui-download" size={16} /> product_materials.jsonl
          </a>
        </span>
      ),
    },
  ];

  return (
    <div className="xz-adm">
      <PageHeader
        title="导出"
        description="先选范围跑一次审计，红线全绿之后才能冻结版本。冻结后的版本不可回改。"
        actions={
          <Button
            leadingIcon={<Icon id="ui-shield" size={16} />}
            disabled={!hasSelection}
            loading={runPreview.isPending}
            onClick={() => runPreview.mutate()}
          >
            预览审计
          </Button>
        }
      />

      <Section title="选择范围" subtitle="按批次多选，或直接粘贴录音编号">
        <div className="xz-form">
          {batches.isPending ? (
            <LoadingBlock rows={2} label="正在加载批次" />
          ) : batches.isError ? (
            <ErrorNotice
              error={batches.error}
              onRetry={() => void batches.refetch()}
              title="批次没能加载"
            />
          ) : (
            <CheckboxPicker
              label="批次"
              searchable
              searchPlaceholder="搜索批次编号"
              options={(batches.data?.batches ?? []).map((b) => ({
                value: b.id,
                label: b.id,
                hint: `${b.phase} · ${BATCH_STATE_LABEL[b.state]} · 录音 ${b.recording_count}`,
              }))}
              value={batchIds}
              onChange={setBatchIds}
              emptyText="还没有批次。"
            />
          )}

          <Field
            label="录音编号（可选）"
            hint="一行一个，也可以用逗号或空格分隔；与批次选择合并去重。"
          >
            <Textarea
              rows={4}
              value={idsText}
              placeholder={"ZY-T2-S017-0003\nZY-T6-S041-0007"}
              onChange={(e) => setIdsText(e.target.value)}
            />
          </Field>

          <p className="gz-caption">
            已选 {batchIds.length} 个批次、{recordingIds.length} 条录音编号。
          </p>

          {!hasSelection && (
            <div className="gz-notice" role="status">
              <Icon id="ui-info" size={20} />
              <span>先选至少一个批次或粘贴一条录音编号，再跑审计。</span>
            </div>
          )}
        </div>
      </Section>

      {preview && (
        <Section
          title="审计结果"
          subtitle={preview.ok ? "红线全绿，可以冻结" : "存在红线错误，先修好再导出"}
          actions={
            <Button
              disabled={!canFreeze}
              variant={canFreeze ? "primary" : "secondary"}
              leadingIcon={<Icon id="ui-lock" size={16} />}
              onClick={() => setFreezeOpen(true)}
            >
              冻结版本
            </Button>
          }
        >
          <StatRow
            label="审计计数"
            items={[
              { key: "rows", icon: "ui-list", label: "总行数", value: preview.rows },
              { key: "train", icon: "ui-check-circle", label: "训练行", value: preview.train_rows },
              {
                key: "product",
                icon: "ui-folder",
                label: "教学素材行",
                value: preview.product_rows,
              },
              { key: "errors", icon: "ui-error", label: "红线错误", value: preview.errors.length },
              {
                key: "warnings",
                icon: "ui-warning",
                label: "警告",
                value: preview.warnings.length,
              },
            ]}
          />

          {preview.errors.length === 0 ? (
            <div
              className="gz-notice gz-notice--success"
              role="status"
              style={{ marginTop: "var(--gz-sp-4)" }}
            >
              <Icon id="ui-check-circle" size={20} />
              <span>0 条错误，{preview.rows} 行可以冻结为一个版本。</span>
            </div>
          ) : (
            <div className="xz-exp__groups" style={{ marginTop: "var(--gz-sp-4)" }}>
              <FindingGroups groups={errorGroups} tone="danger" heading="红线错误" />
            </div>
          )}

          {warningGroups.length > 0 && (
            <div className="xz-exp__groups" style={{ marginTop: "var(--gz-sp-4)" }}>
              <FindingGroups groups={warningGroups} tone="warning" heading="警告（不阻塞冻结）" />
            </div>
          )}
        </Section>
      )}

      <Section title="历史版本" subtitle="冻结之后不可回改">
        <QueryBoundary
          isPending={versions.isPending}
          isError={versions.isError}
          error={versions.error}
          onRetry={() => void versions.refetch()}
          skeletonRows={2}
        >
          <Table
            caption="导出版本历史"
            columns={versionColumns}
            rows={versions.data?.exports ?? []}
            rowKey={(v) => v.version}
            empty={
              <EmptyState
                title="还没有冻结过版本"
                description="跑一次审计，全绿之后就能冻结第一个版本。"
              />
            }
          />
        </QueryBoundary>
      </Section>

      {freezeOpen && preview && (
        <FreezeSheet
          preview={preview}
          batchIds={batchIds}
          onClose={() => setFreezeOpen(false)}
          // Only refresh 历史版本 here. Closing the sheet is `onClose`'s job —
          // doing it on success unmounted the sheet before its 「版本 X 已冻结」
          // panel (and the two download links) could ever render.
          onDone={() => void qc.invalidateQueries({ queryKey: qk.exportVersions })}
        />
      )}
    </div>
  );
}

function FindingGroups({
  groups,
  tone,
  heading,
}: {
  groups: Array<{ code: string; items: AuditFinding[] }>;
  tone: "danger" | "warning";
  heading: string;
}) {
  return (
    <>
      <h3 className="gz-type-heading">{heading}</h3>
      {groups.map((g) => (
        <section key={g.code} className="xz-exp__group">
          <header className="xz-exp__grouphead">
            <Icon id={tone === "danger" ? "ui-error" : "ui-warning"} size={20} />
            <span>{auditCodeLabel(g.code)}</span>
            <StatusTag icon="ui-hash" tone={tone}>
              {g.items.length} 条
            </StatusTag>
          </header>
          <ul className="xz-exp__rows">
            {g.items.map((f, i) => (
              <li key={`${f.recording_id}-${i}`} className="xz-exp__row">
                <span className="xz-exp__rid xz-adm__mono">{f.recording_id}</span>
                <span>{f.message}</span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </>
  );
}

function FreezeSheet({
  preview,
  batchIds,
  onClose,
  onDone,
}: {
  preview: ExportPreviewResponse;
  batchIds: string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [version, setVersion] = useState("1.0.0");
  const [note, setNote] = useState("");
  const [result, setResult] = useState<{ version: string } | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const invalidVersion = !SEMVER.test(version.trim());

  const run = useMutation({
    mutationFn: () =>
      exportApi.freeze({
        version: version.trim(),
        recording_ids: preview.recording_ids,
        note: note.trim() || null,
        ...(batchIds.length > 0 ? { batch_ids: batchIds } : {}),
      }),
    onSuccess: (r) => {
      setResult({ version: r.export.version });
      toast.success(`版本 ${r.export.version} 已冻结，共 ${r.export.rows} 行`);
      onDone();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (result) {
    return (
      <Sheet
        open
        onClose={onClose}
        title={`版本 ${result.version} 已冻结`}
        footer={
          <Button variant="secondary" onClick={onClose}>
            完成
          </Button>
        }
      >
        <div className="xz-form">
          <div className="gz-notice gz-notice--success" role="status">
            <Icon id="ui-check-circle" size={20} />
            <span>这个版本已经不可回改，下面是导出文件。</span>
          </div>
          <div className="xz-exp__links">
            <a
              className="gz-button gz-button--primary"
              href={exportApi.downloadUrl(result.version, "manifest")}
              download
            >
              <Icon id="ui-download" size={16} /> 下载 manifest.jsonl
            </a>
            <a
              className="gz-button"
              href={exportApi.downloadUrl(result.version, "product_materials")}
              download
            >
              <Icon id="ui-download" size={16} /> 下载 product_materials.jsonl
            </a>
          </div>
        </div>
      </Sheet>
    );
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title="冻结导出版本"
      dismissible={!run.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={run.isPending}>
            取消
          </Button>
          <Button
            variant="danger"
            disabled={invalidVersion || !confirmed}
            loading={run.isPending}
            onClick={() => run.mutate()}
          >
            确认冻结
          </Button>
        </>
      }
    >
      <div className="xz-form">
        <div className="gz-notice" role="status">
          <Icon id="ui-info" size={20} />
          <span>
            将冻结 {preview.rows} 行（训练 {preview.train_rows} · 教学素材 {preview.product_rows}
            ）， 审计 0 错误。冻结后不可撤销。
          </span>
        </div>

        <Field
          label="版本号"
          required
          hint="语义化版本，例如 1.0.0"
          {...(invalidVersion ? { error: "版本号必须形如 1.0.0" } : {})}
        >
          <Input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="1.0.0" />
        </Field>

        <Field label="备注" hint="写清楚这个版本的范围与用途，方便日后追溯。">
          <Textarea
            rows={3}
            value={note}
            maxLength={1000}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>

        <label className="xz-form__choice">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
          <span>
            <span>我确认这个版本可以冻结</span>
            <span className="gz-caption">冻结之后 manifest 与素材清单都不可再改。</span>
          </span>
        </label>
      </div>
    </Sheet>
  );
}
